<template>
  <div
    class="ui-progress"
    :class="{ active: running, done: finishing }"
    role="progressbar"
    aria-label="Chargement de la page"
    :aria-hidden="!running"
    :aria-valuenow="Math.round(progress * 100)"
    aria-valuemin="0"
    aria-valuemax="100"
  >
    <div class="ui-progress-bar" :style="{ transform: `scaleX(${progress})` }">
      <span class="ui-progress-glow"></span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, onBeforeUnmount } from "vue";
import { loadingState } from "@/utils/loading";

// NProgress-style bar: jumps in, trickles toward 90% while work is pending,
// completes to 100% and fades out when everything is done.
export default defineComponent({
  name: "TopProgressBar",
  setup() {
    const progress = ref(0);
    const running = ref(false);
    const finishing = ref(false);
    let trickle: ReturnType<typeof setInterval> | undefined;
    let resetTimer: ReturnType<typeof setTimeout> | undefined;

    function start() {
      clearTimeout(resetTimer);
      finishing.value = false;
      running.value = true;
      progress.value = Math.max(progress.value, 0.08);
      clearInterval(trickle);
      trickle = setInterval(() => {
        // slower as it approaches 90%
        const remaining = 0.9 - progress.value;
        if (remaining > 0) progress.value += remaining * (0.08 + Math.random() * 0.07);
      }, 250);
    }

    function finish() {
      clearInterval(trickle);
      progress.value = 1;
      finishing.value = true;
      resetTimer = setTimeout(() => {
        running.value = false;
        finishing.value = false;
        progress.value = 0;
      }, 400);
    }

    watch(
      () => loadingState.visible,
      (visible) => (visible ? start() : finish())
    );

    onBeforeUnmount(() => {
      clearInterval(trickle);
      clearTimeout(resetTimer);
    });

    return { progress, running, finishing };
  },
});
</script>

<style scoped>
.ui-progress {
  position: fixed;
  z-index: 2000;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 300ms var(--ui-ease);
}
.ui-progress.active {
  opacity: 1;
}
.ui-progress.active.done {
  opacity: 0;
  transition-delay: 150ms;
}
.ui-progress-bar {
  position: relative;
  height: 100%;
  transform-origin: left center;
  border-radius: 0 var(--ui-radius-pill) var(--ui-radius-pill) 0;
  background: linear-gradient(90deg, var(--ui-chart-3) 0%, var(--ui-accent) 55%, var(--ui-accent-strong) 100%);
  box-shadow: 0 0 10px rgba(var(--ui-accent-rgb), 0.55);
  transition: transform 250ms var(--ui-ease);
}
/* shimmering head of the bar */
.ui-progress-glow {
  position: absolute;
  top: -3px;
  right: 0;
  width: 90px;
  height: 9px;
  border-radius: 50%;
  background: radial-gradient(ellipse at right, rgba(var(--ui-accent-rgb), 0.55), transparent 70%);
  filter: blur(2px);
}

@media (prefers-reduced-motion: reduce) {
  .ui-progress-bar { transition: none; }
}
</style>
